import {
  executeMemberImport,
  getMemberImportSimulation,
  type MemberImportExecutionResponse,
  MemberImportSimulationApiError,
  type MemberImportSimulationAction,
  type MemberImportSimulationResponse,
  type MemberImportSimulationRow,
} from '@urbanik/api-client';
import { DataTable, StatePanel, canonicalStates, type DataColumn } from '@urbanik/ui';
import { useEffect, useMemo, useState } from 'react';
import { apiBase } from './api-base';
import './member-import-diagnostic.css';
type State = 'loading' | 'success' | 'empty' | 'error' | 'forbidden';
const stateDef = (kind: State) => canonicalStates.find((state) => state.kind === kind)!;
const labels: Record<MemberImportSimulationAction, string> = {
  create: 'Alta',
  update: 'Actualización',
  omit: 'Pendiente de revisión',
};
const columns: readonly DataColumn<MemberImportSimulationRow>[] = [
  {
    id: 'slotNumber',
    header: 'N.º de cupo',
    accessor: (row) => row.slotNumber ?? '—',
    cell: (row) => <strong>{row.slotNumber ?? '—'}</strong>,
  },
  { id: 'row', header: 'Fila Excel', accessor: (row) => String(row.rowNumber) },
  { id: 'member', header: 'Socio', accessor: (row) => row.fullName ?? '—' },
  {
    id: 'action',
    header: 'Impacto simulado',
    accessor: (row) => labels[row.action],
    cell: (row) => <strong>{labels[row.action]}</strong>,
  },
  { id: 'reason', header: 'Explicación', accessor: (row) => row.reason },
];
export function MemberImportSimulationView({ canExecute = false }: { canExecute?: boolean }) {
  const [attempt, setAttempt] = useState(0),
    [state, setState] = useState<State>('loading'),
    [data, setData] = useState<MemberImportSimulationResponse | null>(null),
    [filter, setFilter] = useState<'all' | MemberImportSimulationAction>('all'),
    [executing, setExecuting] = useState(false),
    [execution, setExecution] = useState<MemberImportExecutionResponse | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    getMemberImportSimulation(apiBase, controller.signal)
      .then((result) => {
        setData(result);
        setState(result.total ? 'success' : 'empty');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState(
          error instanceof MemberImportSimulationApiError && error.kind === 'forbidden'
            ? 'forbidden'
            : 'error',
        );
      });
    return () => controller.abort();
  }, [attempt]);
  const visibleRows = useMemo(
    () => data?.rows.filter((row) => filter === 'all' || row.action === filter) ?? [],
    [data, filter],
  );
  if (state !== 'success' || !data)
    return state === 'error' ? (
      <StatePanel state={stateDef(state)} onAction={() => setAttempt((value) => value + 1)} />
    ) : (
      <StatePanel state={stateDef(state)} />
    );
  return (
    <div className="shell-page import-diagnostic-page">
      <header className="page-heading">
        <div>
          <p className="kicker">M04.4 · Simulación sin escritura</p>
          <h1>Impacto antes de importar</h1>
          <p>Compara la fuente histórica con la base actual sin crear ni modificar registros.</p>
        </div>
      </header>
      <section className="diagnostic-summary" aria-label="Resumen de impacto">
        <article>
          <span>Altas</span>
          <strong>{data.creates}</strong>
        </article>
        <article>
          <span>Actualizaciones</span>
          <strong>{data.updates}</strong>
        </article>
        <article>
          <span>Pendientes preservadas</span>
          <strong>{data.preservedForReview}</strong>
        </article>
      </section>
      <p role="status">
        <strong>Es una simulación:</strong> no se realizó ninguna escritura.
      </p>
      <p>
        Las {data.total} filas de origen están contempladas: {data.creates + data.updates} pasarían
        al padrón operativo y {data.preservedForReview} quedarían pendientes, sin descartarse.
      </p>
      {execution ? (
        <section className="diagnostic-file" aria-label="Resultado de importación">
          <div>
            <span>Estado</span>
            <strong>Lote completado</strong>
          </div>
          <div>
            <span>Importados</span>
            <strong>{execution.imported}</strong>
          </div>
          <div>
            <span>Pendientes preservadas</span>
            <strong>{execution.preservedForReview}</strong>
          </div>
          <div>
            <span>Idempotencia</span>
            <strong>
              {execution.duplicateBlocked ? 'Duplicado bloqueado' : 'Primera ejecución'}
            </strong>
          </div>
        </section>
      ) : null}
      {canExecute ? (
        <button
          type="button"
          className="primary-action"
          disabled={executing || execution !== null}
          onClick={() => {
            setExecuting(true);
            void executeMemberImport(apiBase)
              .then(setExecution)
              .finally(() => setExecuting(false));
          }}
        >
          {executing
            ? 'Importando…'
            : execution
              ? 'Importación completada'
              : `Confirmar e importar ${data.total} filas`}
        </button>
      ) : null}
      <nav className="preview-filters" aria-label="Filtros de simulación">
        {(
          [
            ['all', `Todas (${data.total})`],
            ['create', `Altas (${data.creates})`],
            ['update', `Actualizaciones (${data.updates})`],
            ['omit', `Pendientes (${data.preservedForReview})`],
          ] as const
        ).map(([value, label]) => (
          <button
            type="button"
            className={filter === value ? 'active' : ''}
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
            key={value}
          >
            {label}
          </button>
        ))}
      </nav>
      <DataTable
        columns={columns}
        rows={visibleRows}
        visibleColumnIds={new Set(columns.map((column) => column.id))}
        sort={{ columnId: 'row', direction: 'ascending' }}
        onSort={() => {}}
        empty={<StatePanel state={stateDef('empty')} />}
      />
    </div>
  );
}
